import { Injectable } from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgForm,
} from "@angular/forms";
import { ErrorStateMatcher } from "@angular/material/core";
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from "@angular/material/dialog";
import { Subscription, Observable } from "rxjs";
import { DownloadComponent } from "src/app/shared/components/download/download.component";
import { SearchPipe } from "src/app/shared/search-pipe";
import { ReportFiltersService } from "../reports/report-details/report-filters/report-filters.service";
import { ToWords } from "./toWords/toWords";
import { DatePipe } from "@angular/common";
import { SafeHtmlPipe } from "src/app/shared/components/safe-html.pipe";
import { catchError } from "rxjs/operators";
import { deepCopy } from "@angular-devkit/core/src/utils/object";
import { LoanApplicationStatusComponent } from "../loans/loan-application/loan-application-status/loan-application-status.component";
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from "@angular/material/snack-bar";

/** Error when invalid control is dirty, touched, or submitted. */
export class TplMISFormErrorMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    const isSubmitted = form && form.submitted;
    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || isSubmitted)
    );
  }
}

@Injectable({
  providedIn: "root",
})
export class MixinFunctionsService {
  subs: Subscription[] = [];
  selectedAdminArea: any;
  searchKey: string;
  customDatePipe!: any;
  applicationStatusRef: MatDialogRef<LoanApplicationStatusComponent>;
  horizontalPosition: MatSnackBarHorizontalPosition = "right";
  verticalPosition: MatSnackBarVerticalPosition = "top";

  http: any;
  constructor(
    private dialog: MatDialog,
    private searchPipe: SearchPipe,
    private financialYears: ReportFiltersService,
    private datePipe: DatePipe,
    private safeHtml: SafeHtmlPipe,
    private reportFiltersService: ReportFiltersService,
    private snackBar: MatSnackBar
  ) {}

  toWord = new ToWords({
    localeCode: "en-IN",
    converterOptions: {
      currency: true,
      ignoreDecimal: false,
      ignoreZeroCurrency: false,
    },
  });

  getFileSize(bytes: number, decimals = 2) {
    if (bytes === 0) {
      return "0 Bytes";
    }

    const k = 1000;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  base64FileTypes(base64: string | string[]) {
    let fileTypeName = [];

    const signatures = {
      JVBERi0: "application/pdf",
      R0lGODdh: "image/gif",
      R0lGODlh: "image/gif",
      iVBORw0KGgo: "image/png",
      "0M8R4KGxGu": "application/msword",
    };

    for (const s in signatures) {
      if (base64.indexOf(s) === 0) {
        fileTypeName = signatures[s];
      }
    }
  }

  numberToWords(int: number) {
    if (int === 0 || int === null || int === undefined) {
      return "zero";
    }

    const ONES = [
      "",
      "one",
      "two",
      "three",
      "four",
      "five",
      "six",
      "seven",
      "eight",
      "nine",
      "ten",
      "eleven",
      "twelve",
      "thirteen",
      "fourteen",
      "fifteen",
      "sixteen",
      "seventeen",
      "eighteen",
      "nineteen",
    ];

    const TENS = [
      "",
      "",
      "twenty",
      "thirty",
      "fourty",
      "fifty",
      "sixty",
      "seventy",
      "eighty",
      "ninety",
    ];

    const BIGS = [
      "",
      "thousand",
      "million",
      "billion",
      "trillion",
      "quadrillion",
      "quintillion",
      "sextillion",
      "septillion",
      "octillion",
      "nonillion",
    ];

    /**
     * Return string of first three digits, padded with zeros if needed
     */
    function getFirst(str: string) {
      return ("000" + str).substr(-3);
    }

    /**
     * Return string of digits with first three digits chopped off
     */

    function getRest(str: string) {
      return str.substr(0, str.length - 3);
    }

    /**
     * Return string of triplet convereted to words
     */
    function tripletToWords(
      third: string,
      second: string | number,
      first: string
    ) {
      return (
        (third === "0" ? "" : ONES[third] + " hundred ") +
        (first === "0"
          ? TENS[second]
          : (TENS[second] && TENS[second] + "-") || "") +
        (ONES[second + first] || ONES[first])
      );
    }

    /**
     * Add to words, triplet words with scale word
     */
    function addToWords(
      words: string,
      tripletWords: string,
      scaleWord: string
    ) {
      return tripletWords
        ? tripletWords + ((scaleWord && " " + scaleWord) || "") + " " + words
        : words;
    }

    function iterate(words: string, i: number, first, rest) {
      if (first === "000" && rest.length === 0) {
        return words;
      }
      return iterate(
        addToWords(
          words,
          tripletToWords(first[0], first[1], first[2]),
          BIGS[i]
        ),
        ++i,
        getFirst(rest),
        getRest(rest)
      );
    }
    return iterate("", 0, getFirst(String(int)), getRest(String(int)));
  }

  amountSeparator(amount) {
    if (amount === null || amount === undefined) {
      return 0;
    }
    if (amount !== amount || amount === "") {
      return;
    } else {
      return parseFloat(amount)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
  }

  titleCase(str) {
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  }

  get maxDate() {
    return new Date().toISOString().slice(0, 10);
  }

  allowedDates = (d: Date | null): boolean => {
    const day = (d || new Date()).getDay();
    // Prevent Saturday and Sunday
    return day !== 0 && day !== 6;
  };
  /** angular selct option default binding comperator */
  comparator(c1: any, c2: any) {
    //console.log("comparator", 'C1:', c1, "C2:", c2, 'found:', c1 === c2);
    if (c1.id && c2.id) {
      //when object contains  an ids

      return c1 && c2 ? c1.id === c2.id : c1 === c2;
    } else {
      return c1 && c2 ? c1 === c2 : c1 === c2; // when object has no Id
    }
  }

  /**
   *
   * @param form
   * @param control
   * @param error
   * @returns
   * validate from control name and return errors
   */
  public validateForm = (form: FormGroup, control: string, error: string) => {
    return form.controls[control].hasError(error);
  };

  toUppercase = (form: FormGroup, formControlName: string) => {
    const obj = {};
    obj[formControlName] = form.controls[formControlName].value.toUpperCase();
    form.patchValue(obj);
  };

  toLowerCase = (form: FormGroup, formControlName: string) => {
    const obj = {};
    obj[formControlName] = form.controls[formControlName].value.toLowerCase();
    form.patchValue(obj);
  };

  applicationStatus = (status) => {
    switch (status.toLowerCase()) {
      case "initiated":
        return "rgb(16,108,20)";

      case "submitted":
        return "rgb(38, 166, 154)";

      case "returned":
        return "rgb(180,17,70)";

      case "changed":
        return "rgb(219,112,147)";
      case "pending":
        return "rgb(219,112,147)";

      case "re-submitted":
        return "rgb(66,0,205)";

      case "verified":
        return "rgb(44, 178, 44)";

      case "approved":
        return "rgb(29, 00, 201)";

      case "declined":
        return "rgb(190,0,200)";

      case "rejected":
        return "rgb(255,8,0)";
    }
  };

  download(data) {
    const linkSource =
      "data:application/msword;base64," + data && data.fileBase64
        ? data.fileBase64
        : data;

    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = data && data.name ? data.name : data;
    downloadLink.click();
  }

  downloadAttachments(param) {
    //console.log("element", param);
    const data = {
      title: param && param.name ? param.name : "Download",
      action: "create",
      dataBinary: param && param.fileBase64 ? param.fileBase64 : param,
    };

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.width = "70%";
    dialogConfig.height = "80%";
    dialogConfig.data = data;
    dialogConfig.panelClass = "mat-dialog-box";
    const dl = this.dialog.open(DownloadComponent, dialogConfig);
    this.subs.push(
      dl.afterClosed().subscribe(
        (response: any) => {},
        (error) => {}
      )
    );
  }

  toWords(num) {
    if (num === 0 || num === null || num === undefined) {
      return "zero";
    }
    if (num < 0 || num === null || num === undefined) {
      return "";
    }

    return this.toWord.convert(num, { currency: true, ignoreDecimal: false });
  }

  divScroll(e) {
    //
    //console.log("divScroll", e);
    return Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight,
      document.body.clientHeight,
      document.documentElement.clientHeight
    );
  }

  filterData(event: Event, list: any = [], unfiltered: any = []) {
    const filterValue = (event.target as HTMLInputElement).value;

    return this.searchPipe.transform(list, filterValue, unfiltered);
  }

  concatenate(str: any[]) {
    return "".concat(str.join(" "));
  }

  cleanObjects(obj) {
    for (var keyName in obj) {
      if (obj[keyName] === null || obj[keyName] === undefined) {
        delete obj[keyName];
      }
    }
    return obj;
  }

  public matcher = new TplMISFormErrorMatcher();

  getCurrentFy() {
    this.subs.push(
      this.financialYears.getFinancialYears().subscribe((response) => {
        if (response.code === 200 && response.data !== null) {
          const fy =
            response && response.data.find((key) => key.active === true);

          localStorage.setItem("cFy", JSON.stringify(fy));
        } else {
          this.reloadFy();
        }
      })
    );
  }

  reloadFy() {
    this.getCurrentFy();
  }

  formateDate(date, format) {
    //format 'YYYY-MM-dd

    return this.datePipe.transform(date, `${format}`);
  }

  sanitizeHtml = (data: String | null): String => {
    return this.safeHtml.transform(data);
  };

  get currentUser() {
    const user = JSON.parse(localStorage.getItem("userInfo"));
    return user && user.data && user.data.principal
      ? user.data.principal
      : null;
  }

  printDiv(divId: string) {
    const css = `<link rel="stylesheet" href="https://stackpath.bootstrapcdn.com/bootstrap/4.1.0/css/bootstrap.min.css"
    integrity="sha384-9gVQ4dYFwwWSjIDZnLEWnxCjeSWFphJiwGPXr1jddIhOegiu1FwO5qRGvFXOdJZ4" crossorigin="anonymous">`;
    const printContents = document.getElementById(divId).innerHTML;
    const pageContent = `<!DOCTYPE html><html><head>${css}</head><body onload="window.print()">${printContents}</html>`;
    let popupWindow: Window;
    if (navigator.userAgent.toLowerCase().indexOf("chrome") > -1) {
      popupWindow = window.open(
        "",
        "_blank",
        "scrollbars=no,menubar=no,toolbar=no,location=no,status=no,titlebar=no"
      );
      popupWindow.window.focus();
      popupWindow.document.write(pageContent);
      popupWindow.document.close();
      popupWindow.onbeforeunload = (event) => {
        popupWindow.close();
      };
      popupWindow.onabort = (event) => {
        popupWindow.document.close();
        popupWindow.close();
      };
    } else {
      popupWindow = window.open("", "_blank", "");
      popupWindow.document.open();
      popupWindow.document.write(pageContent);
      popupWindow.document.close();
    }
  }

  printGepgBill(url, data) {
    this.subs.push(
      this.reportFiltersService.getReports(url, data).subscribe((response) => {
        let pdf = new Blob([response], { type: "application/pdf" });
        var fileURL = URL.createObjectURL(pdf);
        window.open(
          fileURL,
          "_blank",
          "location=yes,height=700,width=900,scrollbars=yes,status=yes"
        );
      })
    );
  }

  getFinancialYears(): Observable<any> {
    return this.http
      .get("/api/financialYear/financialYear")
      .pipe(catchError(this.handleError));
  }

  handleError(handleError: any): any {
    throw new Error("Method not implemented.");
  }

  textTruncate(str, n) {
    return str.length > n ? str.substr(0, n - 1) + "..." : str;
  }

  replacer(str: string, toBeReplaced, replaceWith): string {
    let result = "";
    for (var c of str) {
      if (c === toBeReplaced) c = replaceWith;
      result += c;
    }
    return result;
  }

  openApplicationStatus(data): void {
    this.applicationStatusRef = this.dialog.open(
      LoanApplicationStatusComponent,
      {
        width: "1000px",
        backdropClass: "custom-dialog-backdrop-class",
        panelClass: "custom-dialog-panel-class",
        disableClose: true,
        data: {
          ...data,
        },
      }
    );
  }

  openSnackBar(message) {
    this.snackBar.open(message, "CLOSE", {
      horizontalPosition: this.horizontalPosition,
      verticalPosition: this.verticalPosition,
      duration: 5 * 1000,
    });
  }
}
